import { FlexBox, Page, Text } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';
import { useEffectOnce } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { appActions } from 'app/app.actions';
import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { useDashboardRefetchEffect } from 'utils/dashboard';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { DashboardError } from '../../components/error/dashboardError';
import { DashboardHeartbeat } from '../../components/heartbeat/dashboardHeartbeat';

import { ChannelDashboardChannels } from './components/channels/channelDashboardChannels';
import { ChannelDashboardControls } from './components/controls/channelDashboardControls';

export const ChannelDashboardView = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const { isError, refetch } =
    dashboardApi.useReadChannelDashboardMetricsQuery();

  useDashboardRefetchEffect({ refetch });

  useEffectOnce(() => {
    ampli.dashboardViewed({ version: 'channels' });

    dispatch(
      websocketActions.request({
        type: 'connection:subscribe',
        params: {
          type: 'real_time_dashboard',
          dashboard_type: 'channel',
        },
      }),
    );

    return () => {
      dispatch(
        websocketActions.request({
          type: 'connection:unsubscribe',
          params: {
            type: 'real_time_dashboard',
            dashboard_type: 'channel',
          },
        }),
      );
    };
  });

  const handleSendFeedback = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            title: 'Dashboard Feedback',
            team: 'insights',
            extra: {
              dashboard: 'channels',
            },
          },
        },
      }),
    );
  }, []);

  const renderContent = (): JSX.Element => {
    if (isError) {
      return <DashboardError />;
    }

    return <ChannelDashboardChannels />;
  };

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox as="span" alignItems="center">
            Channels real-time dashboard
            <DashboardHeartbeat />
            <Text.ExternalLink onClick={handleSendFeedback} fontSize="200">
              Send feedback
            </Text.ExternalLink>
          </FlexBox>
        }
        controls={<ChannelDashboardControls />}
      />

      <Page.Body>
        <Page.Section>
          <FlexBox flexDirection="column" height="100%">
            {renderContent()}
          </FlexBox>
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
